import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import axios, { AxiosRequestConfig } from 'axios'
import { TRootState } from '..'
import type { BusinessError } from '../../types'
import { TranslatedEnum } from '../../types/TranslatedEnum'
import { ValueListDto, ValueListItem, ValueListItemDto } from '../../api/config/config-api'

interface AppState {
  globalTechnicalError: string | null
  businessErrors: BusinessError[]
  axiosBaseConfig: AxiosRequestConfig
  otherIncome: TranslatedEnum[]
  language: string
  isInitializing: boolean
  withdrawalReasons: ValueListDto | null
  badReviewReasons: ValueListDto | null
}

const initialState: AppState = {
  globalTechnicalError: null,
  businessErrors: [],
  otherIncome: [],
  language: 'fr',
  axiosBaseConfig: {
    baseURL: process.env.REACT_APP_BACKEND_API_URL,
  },
  isInitializing: true,
  withdrawalReasons: null,
  badReviewReasons: null,
}

export const appSlice = createSlice({
  name: 'api',
  initialState,
  reducers: {
    setTechnicalError: (state, action: PayloadAction<string>) => {
      state.globalTechnicalError = action.payload
    },
    setBusinessErrors: (state, action: PayloadAction<BusinessError[]>) => {
      state.businessErrors = action.payload
    },
    setOtherIncome: (state, action: PayloadAction<TranslatedEnum[]>) => {
      state.otherIncome = action.payload
    },
    resetErrors: (state) => {
      state.businessErrors = []
      state.globalTechnicalError = null
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload
    },
    setInitializing: (state, action: PayloadAction<boolean>) => {
      state.isInitializing = action.payload
    },
    setWithdrawalReasons: (state, action: PayloadAction<ValueListDto>) => {
      state.withdrawalReasons = action.payload
    },
    setBadReviewReasons: (state, action: PayloadAction<ValueListDto>) => {
      state.badReviewReasons = action.payload
    },
  },
})

// Action creators are generated for each case reducer function
export const appActions = appSlice.actions

export default appSlice.reducer

// Other code such as selectors can use the imported `RootState` type

export function buildValueList(list: ValueListDto | null, lang: string) {
  if (!list) return []

  const inputList: ValueListItemDto[] = [...list.items]
  if (list.sortMethod === 'priority') inputList.sort((x) => x.priority ?? 0)

  const items: ValueListItem[] = inputList.map((x) => {
    return {
      id: x.value,
      text: lang === 'fr' ? x.textFr : x.textEn,
      contactClient: x.contactClient,
    }
  })
  if (list.sortMethod === 'alpha') items.sort((one, two) => (one.text > two.text ? -1 : 1))
  return items
}

export const appSelectors = {
  getTechnicalError: (state: TRootState) => state.app.globalTechnicalError,
  getBusinessError: (state: TRootState) => state.app.businessErrors,
  getAxiosConfig: (state: TRootState) => state.app.axiosBaseConfig,
  getCurrentLang: (state: TRootState) => state.app.language,
  getOtherIncome: (state: TRootState) => state.app.otherIncome,
  getWithdrawalReasons: createSelector(
    (state: TRootState) => state.app.withdrawalReasons,
    (state: TRootState) => state.app.language,
    buildValueList,
  ),
  getBadReviewReasons: createSelector(
    (state: TRootState) => state.app.badReviewReasons,
    (state: TRootState) => state.app.language,
    buildValueList,
  ),
  getApiClient: createSelector(
    (state: TRootState) => state.app.axiosBaseConfig,
    (config) => {
      return axios.create(config)
    },
  ),
  isLoading: (state: TRootState) => state.app.isInitializing,
}
